<script setup lang="ts">
    import {
        SidebarMenu,
        SidebarMenuButton,
        SidebarMenuItem,
        useSidebar,
    } from '@/components/ui/sidebar';
    import {
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuTrigger,
        DropdownMenuItem,
    } from '@/components/ui/dropdown-menu';
    import { type NavItem, type SharedData } from '@/types';
    import { Link, usePage } from '@inertiajs/vue3';
    import { ChevronsUpDown } from 'lucide-vue-next';
    import { HoverCardContent, HoverCardPortal, HoverCardRoot, HoverCardTrigger } from 'reka-ui';
    import { SIDEBAR_WIDTH } from '@/components/ui/sidebar/utils';
    import { useI18n } from 'vue-i18n';

    defineProps<{
        items: NavItem[];
    }>();

    const page = usePage<SharedData>();
    const { isMobile, state, setOpen } = useSidebar();

    const { t } = useI18n();

    const isActive = (item: NavItem) => {
        const path = `${window.location.origin}/${item.group}`;
        return page.props.ziggy.location.startsWith(path);
        // return page.props.ziggy.location.startsWith(item.href);
    };

    const linkComponent = (item: NavItem) => {
        if (item.component) {
            return item.component;
        }
        if (item.external) {
            return 'a';
        }
        return Link;
    };
</script>

<template>
    <SidebarMenu>
        <SidebarMenuItem
            v-for="item in items"
            :key="item.title"
        >
            <DropdownMenu v-if="item.subItems && isMobile">
                <DropdownMenuTrigger as-child>
                    <SidebarMenuButton
                        class="transition-all duration-300 ease-in-out group-data-[collapsible=icon]:opacity-20 group-data-[collapsible=icon]:data-[active=true]:bg-transparent group-data-[collapsible=icon]:data-[active=true]:opacity-100"
                        :is-active="isActive(item)"
                    >
                        <component :is="item.icon" />
                        <span
                            class="opacity-100 transition-opacity duration-300 ease-in-out group-data-[state=collapsed]:opacity-0"
                            >{{ t(item.title) }}</span
                        >
                        <ChevronsUpDown
                            v-if="state === 'expanded'"
                            class="ml-auto size-4"
                        />
                    </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    class="bg-sidebar w-(--reka-dropdown-menu-trigger-width) min-w-[300px] rounded-none border-none"
                    side="bottom"
                    align="start"
                    :side-offset="7"
                >
                    <DropdownMenuItem
                        v-for="subItem in item.subItems"
                        :key="subItem.title"
                        :as-child="true"
                        class="text-sidebar-foreground hover:text-sidebar-accent-foreground! h-12 hover:bg-transparent!"
                    >
                        <Link
                            class="block w-full"
                            :href="subItem.href"
                            as="button"
                        >
                            {{ t(subItem.title) }}
                        </Link>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <HoverCardRoot
                v-else-if="item.subItems"
                :open-delay="0"
                :close-delay="0"
            >
                <!-- v-model:open="hoverState" -->
                <HoverCardTrigger as-child>
                    <SidebarMenuButton
                        class="data-[state=open]:text-accent transition-all duration-300 ease-in-out group-data-[collapsible=icon]:opacity-20 group-data-[collapsible=icon]:hover:opacity-100 group-data-[collapsible=icon]:data-[active=true]:bg-transparent group-data-[collapsible=icon]:data-[active=true]:opacity-100 data-[state=open]:opacity-100"
                        :is-active="isActive(item)"
                        :data-location="page.props.ziggy.location"
                        :data-href="item.href"
                    >
                        <component :is="item.icon" />
                        <span
                            class="font-normal opacity-100 transition-opacity duration-300 ease-in-out group-data-[state=collapsed]:opacity-0"
                            >{{ t(item.title) }}</span
                        >
                        <ChevronsUpDown
                            v-if="state === 'expanded'"
                            class="ml-auto size-4"
                        />
                    </SidebarMenuButton>
                </HoverCardTrigger>
                <HoverCardPortal>
                    <HoverCardContent
                        class="bg-sidebar z-50 min-w-(--sidebar-width) rounded-none border-none pl-[7px] data-[state=open]:transition-all"
                        :style="{
                            '--sidebar-width': SIDEBAR_WIDTH,
                        }"
                        side="right"
                        align="start"
                    >
                        <Link
                            v-for="subItem in item.subItems"
                            :key="subItem.title"
                            class="focus:text-accent text-sidebar-foreground hover:text-sidebar-accent-foreground! relative flex h-12 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none hover:bg-transparent! data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                            :href="subItem.href"
                            as="button"
                        >
                            {{ t(subItem.title) }}
                        </Link>
                    </HoverCardContent>
                </HoverCardPortal>
            </HoverCardRoot>

            <SidebarMenuButton
                v-else
                as-child
                class="transition-all duration-300 ease-in-out group-data-[collapsible=icon]:opacity-20 group-data-[collapsible=icon]:hover:opacity-100 group-data-[collapsible=icon]:data-[active=true]:bg-transparent group-data-[collapsible=icon]:data-[active=true]:opacity-100"
                :is-active="isActive(item)"
                :tooltip="t(item.title)"
            >
                <component
                    :is="linkComponent(item)"
                    :href="item.href"
                    :target="item.external ? '_blank' : ''"
                    :item="item"
                >
                    <component :is="item.icon" />
                    <span
                        class="font-normal opacity-100 transition-opacity duration-300 ease-in-out group-data-[state=collapsed]:opacity-0"
                        >{{ t(item.title) }}</span
                    >
                </component>
            </SidebarMenuButton>
        </SidebarMenuItem>
    </SidebarMenu>
</template>
